import { Bot, LogIn, UserPlus } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

const NAV = {
  en: { how: "How It Works", pricing: "Pricing", register: "Sign Up", login: "Login", dashboard: "Dashboard" },
  es: { how: "Cómo Funciona", pricing: "Precios", register: "Registro", login: "Login", dashboard: "Dashboard" },
};

export const Navbar = ({ lang, setLang }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const t = NAV[lang] || NAV.en;

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
    else navigate("/");
  };

  return (
    <header className="fixed top-0 w-full z-50 backdrop-blur-xl bg-[#05050A]/70 border-b border-white/5">
      <nav className="max-w-7xl mx-auto flex items-center justify-between px-4 md:px-12 h-16 gap-3">
        <a href="/" data-testid="navbar-logo" className="text-base sm:text-xl font-black tracking-tighter flex items-center gap-2 text-white font-heading whitespace-nowrap shrink-0">
          <span className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-gradient-to-br from-[#B026FF] to-[#00F5FF] flex items-center justify-center shadow-[0_0_15px_rgba(176,38,255,0.5)] shrink-0">
            <Bot size={18} className="text-white" />
          </span>
          <span className="hidden xs:inline sm:inline">AVATAR <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#B026FF] to-[#00F5FF]">4 U</span></span>
        </a>

        <div className="hidden lg:flex items-center gap-1" data-testid="navbar-tabs">
          <button data-testid="nav-tab-how" onClick={() => scrollTo("how-it-works")}
            className="text-xs font-bold uppercase tracking-widest text-white/60 hover:text-white px-4 py-2 rounded-full hover:bg-white/5 transition-colors">
            {t.how}
          </button>
          <button data-testid="nav-tab-pricing" onClick={() => scrollTo("pricing")}
            className="text-xs font-bold uppercase tracking-widest text-white/60 hover:text-white px-4 py-2 rounded-full hover:bg-white/5 transition-colors">
            {t.pricing}
          </button>
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          <div className="flex items-center gap-1 bg-white/5 border border-white/10 rounded-full p-1" data-testid="language-selector">
            {["en", "es"].map((l) => (
              <button
                key={l}
                data-testid={`lang-${l}-btn`}
                onClick={() => setLang(l)}
                className={`text-xs font-bold px-2.5 sm:px-4 py-1.5 rounded-full transition-all duration-300 uppercase tracking-widest ${
                  lang === l
                    ? "bg-gradient-to-r from-[#B026FF] to-[#7B1FD6] text-white shadow-[0_0_12px_rgba(176,38,255,0.5)]"
                    : "text-white/50 hover:text-white"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
          {!user && (
            <Link
              to="/register"
              data-testid="navbar-register-btn"
              className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest px-3 sm:px-5 py-2.5 rounded-full bg-gradient-to-r from-[#B026FF] to-[#7B1FD6] border border-[#D080FF]/40 shadow-[0_0_15px_rgba(176,38,255,0.4)] hover:shadow-[0_0_25px_rgba(176,38,255,0.6)] transition-shadow whitespace-nowrap"
            >
              <UserPlus size={14} />
              <span className="hidden sm:inline">{t.register}</span>
            </Link>
          )}
          <Link
            to={user ? "/dashboard" : "/login"}
            data-testid="navbar-login-btn"
            className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest px-3 sm:px-5 py-2.5 rounded-full bg-white/5 border border-white/15 hover:bg-white/10 transition-colors whitespace-nowrap"
          >
            <LogIn size={14} />
            <span className="hidden sm:inline">{user ? t.dashboard : t.login}</span>
          </Link>
        </div>
      </nav>

      <div className="lg:hidden flex items-center justify-center gap-1 pb-2 px-4" data-testid="navbar-tabs-mobile">
        <button data-testid="nav-tab-how-mobile" onClick={() => scrollTo("how-it-works")}
          className="text-[10px] font-bold uppercase tracking-widest text-white/60 px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
          {t.how}
        </button>
        <button data-testid="nav-tab-pricing-mobile" onClick={() => scrollTo("pricing")}
          className="text-[10px] font-bold uppercase tracking-widest text-white/60 px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
          {t.pricing}
        </button>
        {!user && (
          <Link to="/register" data-testid="nav-tab-register-mobile"
            className="text-[10px] font-bold uppercase tracking-widest text-[#D080FF] px-3 py-1.5 rounded-full bg-[#B026FF]/10 border border-[#B026FF]/30">
            {t.register}
          </Link>
        )}
      </div>
    </header>
  );
};
